import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Mail, Loader2, ChevronDown, Eye, EyeOff } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useAuth } from "../App";
import { ForgotPasswordModal } from "./ForgotPasswordModal";
import { readStoredReferrer } from "../lib/referrals";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Remember the last-used email between visits so returning users only
// have to re-type the password. localStorage (not sessionStorage) so it
// survives full browser restarts — the value is a normal identifier, not
// a secret; browsers already offer this via built-in autofill, we're just
// making it work on first visit without a saved-password prompt.
const REMEMBER_EMAIL_KEY = "bc_last_email_v1";
const readRememberedEmail = () => {
  try { return window.localStorage.getItem(REMEMBER_EMAIL_KEY) || ""; }
  catch { return ""; }
};
const writeRememberedEmail = (email) => {
  try { window.localStorage.setItem(REMEMBER_EMAIL_KEY, email); }
  catch { /* private mode / quota */ }
};

// Map FastAPI's various error shapes (string | {msg} | [{msg,...}]) to a
// single user-facing string — never let a raw object reach JSX.
const formatErr = (detail) => {
  if (!detail) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).join(" ").trim();
  }
  if (detail.msg) return detail.msg;
  return String(detail);
};

// Email/password auth form. Collapsible — hidden by default behind a "Use
// email instead" toggle so the primary Google CTA stays prominent.
export const EmailAuthForm = ({ mode = "signup", compact = false, redirectTo = "/dashboard" }) => {
  // mode: "signup" (returning users want signup CTA → register form)
  //       "signin" (returning users want login CTA → login form)
  // compact: pass true on the dark "New here?" pane so styling adjusts
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [isLogin, setIsLogin] = useState(mode === "signin");
  const [email, setEmail] = useState(() => readRememberedEmail());
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Refs for auto-focus when the form expands: focus password if we
  // already remember the email (returning user), otherwise focus email.
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Focus ONCE when the form opens — read email via ref-style closure so
    // subsequent keystrokes don't retrigger this and steal focus back to
    // the password field. (The old dep-array of [open, email] caused the
    // email input to lose focus after every single character typed.)
    const t = setTimeout(() => {
      if (readRememberedEmail()) passwordRef.current?.focus();
      else emailRef.current?.focus();
    }, 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const path = isLogin ? "/auth/login" : "/auth/register";
      // Include a stashed referrer (from ?ref=<user_id> on landing) on
      // the register path so brand-new accounts get bound to their
      // referrer server-side. Login ignores it.
      const ref = !isLogin ? readStoredReferrer() : null;
      const body = isLogin
        ? { email, password }
        : { email, password, name, ...(ref ? { ref } : {}) };
      const { data } = await axios.post(`${API}${path}`, body);
      setUser(data);
      // Remember the email so next visit skips straight to the password
      // field (see useEffect focus logic).
      writeRememberedEmail(email.trim());
      navigate(redirectTo, { replace: true });
    } catch (err) {
      // Order matters: show server-supplied message first; fall back to a
      // network-friendly message if the request never reached the server
      // (CORS/timeout/offline) so users (and App Review) get a real signal.
      const serverDetail = formatErr(err.response?.data?.detail);
      const msg =
        serverDetail
        || (err.response?.status
              ? `Server error (${err.response.status}). Please try again.`
              : null)
        || (err.message && err.message !== "Network Error"
              ? err.message
              : "Network problem — please check your connection and try again.");
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  // Outer color scheme adapts to where this is mounted:
  // - on the white "Returning" pane → dark text on white
  // - on the dark "New here?" pane → light text on translucent
  const onDark = compact;

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        type="button"
        className={`w-full justify-center rounded-full border font-medium py-3 inline-flex items-center gap-2 transition-colors ${
          onDark
            ? "border-white/30 text-white hover:bg-white/10"
            : "border-slate-200 text-[#1B2349] hover:bg-slate-50"
        }`}
        data-testid="email-auth-open-btn"
      >
        <Mail className="w-4 h-4" />
        {isLogin ? "Sign in with email" : "Sign up with email"}
        <ChevronDown className="w-4 h-4 opacity-60" />
      </button>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="space-y-3 pt-1"
      data-testid={`email-auth-form-${isLogin ? "login" : "register"}`}
    >
      {!isLogin && (
        <Input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name (optional)"
          className={`rounded-xl ${onDark ? "bg-white/10 border-white/20 text-white placeholder:text-white/60" : ""}`}
          data-testid="email-auth-name"
          maxLength={80}
        />
      )}
      <Input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email address"
        required
        autoComplete="email"
        ref={emailRef}
        className={`rounded-xl ${onDark ? "bg-white/10 border-white/20 text-white placeholder:text-white/60" : ""}`}
        data-testid="email-auth-email"
      />
      <div className="relative">
        <Input
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={isLogin ? "Password" : "Password (8+ characters)"}
          required
          autoComplete={isLogin ? "current-password" : "new-password"}
          minLength={isLogin ? 1 : 8}
          ref={passwordRef}
          className={`rounded-xl pr-11 ${onDark ? "bg-white/10 border-white/20 text-white placeholder:text-white/60" : ""}`}
          data-testid="email-auth-password"
        />
        <button
          type="button"
          onClick={() => setShowPassword((v) => !v)}
          aria-label={showPassword ? "Hide password" : "Show password"}
          className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded transition-colors ${
            onDark
              ? "text-white/70 hover:text-white"
              : "text-[#718096] hover:text-[#1B2349]"
          }`}
          data-testid="email-auth-show-password"
          tabIndex={-1}
        >
          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      {error && (
        <p
          className={`text-sm ${onDark ? "text-[#FFB3A8]" : "text-[#D98880]"}`}
          data-testid="email-auth-error"
        >
          {error}
        </p>
      )}
      <Button
        type="submit"
        disabled={busy}
        className={`w-full justify-center rounded-full font-medium py-3 ${
          onDark
            ? "bg-white text-[#1B2349] hover:bg-[#F4F1E8]"
            : "bg-[#1B2349] text-white hover:bg-[#0F1838]"
        }`}
        data-testid="email-auth-submit"
      >
        {busy
          ? <Loader2 className="w-4 h-4 animate-spin" />
          : isLogin ? "Sign in" : "Create account"}
      </Button>
      <button
        type="button"
        onClick={() => { setIsLogin((v) => !v); setError(""); }}
        className={`text-xs underline underline-offset-2 ${
          onDark ? "text-white/70 hover:text-white" : "text-[#3090A8] hover:text-[#1B2349]"
        }`}
        data-testid="email-auth-toggle-mode"
      >
        {isLogin ? "Need an account? Sign up" : "Already have an account? Sign in"}
      </button>
      {isLogin && (
        <button
          type="button"
          onClick={() => setShowForgot(true)}
          className={`block text-xs underline underline-offset-2 ${
            onDark ? "text-white/70 hover:text-white" : "text-[#3090A8] hover:text-[#1B2349]"
          }`}
          data-testid="email-auth-forgot-link"
        >
          Forgot password or username?
        </button>
      )}
      <ForgotPasswordModal
        open={showForgot}
        onOpenChange={setShowForgot}
        initialEmail={email}
      />
    </form>
  );
};

export default EmailAuthForm;
